import { getContextFromPromptV4 } from '@common/Copilot/CopilotAgentChat/agent-v4/getContextFromPromptV4';
import { EntityBadge } from '@common/Copilot/CopilotAgentChat/EntityBadge';
import { renderMentionSuggestion } from '@common/Copilot/CopilotAgentChat/Mentions';
import { getMentionValue } from '@common/Copilot/CopilotAgentChat/utils';
import { EntityIcon } from '@common/Entity/EntityIcons';
import { useUserPipelines } from '@common/pipeline/hooks/useUserPipelines';
import { useUserSQLProjects } from '@common/projectHooks/useUserSQLProjects';
import { Entity } from '@common/types/Entity';
import { parseMentionWithProperties } from '@prophecy/ui-v3/Markdown/mentionUtils';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { upperFirst } from 'lodash-es';

import type { EntityMention } from '@common/Copilot/CopilotAgentChat/Mentions';
import type { EntityIconKey } from '@common/Entity/constants';
import type { SqlProject } from '@common/projectHooks/useUserSQLProjects';

type PipelineLike = { name: string; project?: { _id: string; name: string } | null };

function mention(entityType: Entity, entityId: string, label: string): EntityMention {
  return {
    entityType: entityType as EntityIconKey,
    entityId,
    label,
    value: entityId,
    icon: <EntityIcon entity={entityType as EntityIconKey} variant='singleColor' size='xs' />
  };
}

export function homeMentionSuggestions(
  query: string,
  projects: Pick<SqlProject, 'name'>[],
  pipelines: PipelineLike[]
): EntityMention[] {
  const needle = query.trim().toLowerCase();
  const matches = (name: string) => name.toLowerCase().includes(needle);
  return [
    ...projects.filter((p) => matches(p.name)).map((p) => mention(Entity.Project, p.name, p.name)),
    ...pipelines
      .filter((p) => p.project && matches(p.name))
      .map((p) => mention(Entity.Pipeline, `${p.project!.name}.${p.name}`, p.name))
  ];
}

export function homeMentionContext(prompt: string, projects: SqlProject[]) {
  const context = getContextFromPromptV4(prompt, { datasets: [], projectDatasets: [], projects });
  // The IDE's project mention carries only a name; home chat has no open project to resolve it by.
  for (const value of Object.values(context)) {
    if (value.kind !== Entity.Project) continue;
    const properties = (value.properties ?? {}) as { name?: string };
    const project = projects.find((p) => p.name === properties.name);
    if (project) value.properties = { ...properties, projectId: project._id };
  }
  return context;
}

/** A chip with no click-through: the IDE's EntityMentionNode needs an open project. */
export function renderHomeMention(text: string) {
  const parsed = parseMentionWithProperties(text.replace(/^@/, ''));
  if (!parsed) return text;
  return (
    <EntityBadge
      entityType={upperFirst(parsed.entityType) as EntityIconKey}
      value={parsed.entityId}
      badgeText={parsed.displayText}
      isMessageEntity={false}
    />
  );
}

export function useHomeChatMentions() {
  const { projects } = useUserSQLProjects();
  const { sqlPipelines } = useUserPipelines();
  const getMentionSuggestions = usePersistentCallback(async (query: string) =>
    homeMentionSuggestions(query, projects, sqlPipelines)
  );
  const contextFor = usePersistentCallback((prompt: string) => homeMentionContext(prompt, projects));
  return {
    getMentionSuggestions,
    getMentionValue,
    renderMention: renderHomeMention,
    renderMentionSuggestion,
    contextFor
  };
}
